import type {
  FileAsset,
  GetRequestByIdResponse,
  LinkAsset,
} from "@nlc/shared-types";
import type {
  DefaultValues,
  FieldValues,
  UseFormReturn,
} from "react-hook-form";

import { BriefCreatorTypeEnum, RequestTypeEnum } from "@nlc/shared-types";
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useRef } from "react";

import { useIsMBCSubmitted } from "@/features/request/create/shared/useIsMBCSubmitted.tsx";
import { useGetAttachments } from "@/features/request/edit/hooks/useGetAttachments";
import { mapAttachmentsToAssets } from "@/features/request/edit/utils/utils";
import { useGetRequest } from "@/hooks/useGetRequest.tsx";

import type { CreateGraphicRequestFormData } from "../graphic/components/useCreateGraphicRequestForm";
import type { CreateVideoRequestFormData } from "../video/components/useCreateVideoRequestForm";
import type { CreateWebsiteDesignRequestFormData } from "../website-design/components/useCreateWebsiteDesignRequestForm";
import type { CreateWebsiteImplementationRequestFormData } from "../website-implementation/components/useCreateWebsiteImplementationRequestForm";

type RequestInfo = {
  assets: Array<FileAsset | LinkAsset>;
  inspirations: Array<FileAsset | LinkAsset>;
} & GetRequestByIdResponse;

/**
 * Hydrates the form with data from an existing draft request.
 *
 * @remarks
 * This hook is responsible for populating the form fields when editing an existing draft
 * (e.g., when returning to a previously saved draft from another session).
 *
 * **Behavior**:
 * - If a draft_id is in the URL, fetches the draft data and resets the form with it
 * - If the request is not a draft, redirects to the regular edit page
 * - Sets MBC submission state if the draft uses the detailed brief creator
 *
 * @param formMethods - React Hook Form methods for resetting the form
 * @param requestType - The type of request being created
 * @returns `true` if currently editing a draft, `false` otherwise
 */
export function useLoadDraftData<T extends FieldValues>({
  formMethods,
  requestType,
}: {
  formMethods: Pick<UseFormReturn<T>, "reset" | "setValue">;
  requestType: RequestTypeEnum;
}): boolean {
  const navigate = useNavigate();
  const setIsMBCSubmitted = useIsMBCSubmitted((state) => state.setIsSubmitted);

  const { data: request } = useGetRequest();
  const { data: attachments } = useGetAttachments();

  const isEditingExistingDraft = Boolean(request?.isDraft);
  const hydratedRequestIdRef = useRef<number>();

  useEffect(() => {
    if (request && isEditingExistingDraft && attachments) {
      if (hydratedRequestIdRef.current === request.id) return;

      hydratedRequestIdRef.current = request.id;

      const { assets, example: inspirations } =
        mapAttachmentsToAssets(attachments);

      const requestWithMappedAssets = { ...request, assets, inspirations };

      const mappedRequest = mapDraftRequestToFormData(
        requestWithMappedAssets,
        requestType,
      );

      formMethods.reset(mappedRequest as DefaultValues<T>, {
        keepDefaultValues: true,
        keepDirty: false,
        keepErrors: false,
        keepIsSubmitted: false,
        keepIsValid: false,
        keepSubmitCount: false,
        keepTouched: false,
      });

      if (
        determineBriefCreatorType(requestWithMappedAssets) ===
        BriefCreatorTypeEnum.DETAILED
      ) {
        setIsMBCSubmitted(true);
      }
    }
  }, [request, isEditingExistingDraft, requestType, attachments]);

  useEffect(() => {
    if (request && !request.isDraft) {
      void navigate({ to: `/requests/${request.id}/edit` });
    }
  }, [request, navigate]);

  return isEditingExistingDraft;
}

function mapDraftRequestToFormData(
  data: RequestInfo,
  requestType: RequestTypeEnum,
):
  | DefaultValues<CreateGraphicRequestFormData>
  | DefaultValues<CreateVideoRequestFormData>
  | DefaultValues<CreateWebsiteDesignRequestFormData>
  | DefaultValues<CreateWebsiteImplementationRequestFormData> {
  switch (requestType) {
    case RequestTypeEnum.GRAPHIC: {
      return mapGraphicDraftRequestToFormData(data);
    }
    case RequestTypeEnum.VIDEO: {
      return mapVideoDraftRequestToFormData(data);
    }
    case RequestTypeEnum.WEBSITE_DESIGN: {
      return mapWebsiteDesignDraftRequestToFormData(data);
    }
    case RequestTypeEnum.WEBSITE_IMPLEMENTATION: {
      return mapWebsiteImplementationDraftRequestToFormData(data);
    }
    default: {
      throw new Error(`Unsupported request type: ${requestType}`);
    }
  }
}

function mapGraphicDraftRequestToFormData(
  data: RequestInfo,
): DefaultValues<CreateGraphicRequestFormData> {
  const briefCreatorType = determineBriefCreatorType(data);

  return {
    assets: data.assets || [],
    brand: data.brandId || undefined,
    briefCreatorDetailed: {
      additionalGuidelines:
        data.magicalBriefCreator?.additionalGuidelines || "",
      brandGuidelines: data.magicalBriefCreator?.brandGuidelines || "",
      creativeGoal: data.magicalBriefCreator?.creativeGoals || "",
      finalBrief: data.briefText || "",
      finalBriefHTML: data.briefText || "",
      keyMessageAndCta: data.magicalBriefCreator?.keyMessageAndCta || "",
      scope: data.magicalBriefCreator?.scope || "",
    },
    briefCreatorSingle: {
      requestDescription: data.description || "",
      requestDescriptionHTML: data.description || "",
      textsForDesign: data.textForDesign || "",
      textsForDesignHTML: data.textForDesign || "",
    },
    briefCreatorType,
    collaboratorIds: data.collaboratorsIds || [],
    creativeDirection: data.creativeDirection || undefined,
    designComplexity: data.complexityId || undefined,
    designType: data.designTypeId || undefined,
    fileTypes: data.preferredFileType || "",
    inspirations: data.inspirations || [],
    preferredDesignerIds: data.preferredDesignerIds || [],
    requestName: data.name || "",
    restrictAiAssets: data.restrictAiAssets ?? false,
    sizes: data.sizesNeeded || "",
  };
}

function mapVideoDraftRequestToFormData(
  data: RequestInfo,
): DefaultValues<CreateVideoRequestFormData> {
  const briefCreatorType = determineBriefCreatorType(data);

  return {
    assets: data.assets || [],
    brand: data.brandId || undefined,
    briefCreatorDetailed: {
      additionalGuidelines:
        data.magicalBriefCreator?.additionalGuidelines || "",
      brandGuidelines: data.magicalBriefCreator?.brandGuidelines || "",
      creativeGoal: data.magicalBriefCreator?.creativeGoals || "",
      finalBrief: data.briefText || "",
      finalBriefHTML: data.briefText || "",
      scope: data.magicalBriefCreator?.scope || "",
      script: data.magicalBriefCreator?.script || "",
    },
    briefCreatorSingle: {
      requestDescription: data.description || "",
      requestDescriptionHTML: data.description || "",
      textsForDesign: data.textForDesign || "",
      textsForDesignHTML: data.textForDesign || "",
    },
    briefCreatorType,
    collaboratorIds: data.collaboratorsIds || [],
    creativeDirection: data.creativeDirection || undefined,
    designComplexity: data.complexityId || undefined,
    fileTypes: data.preferredFileType || "",
    inspirations: data.inspirations || [],
    lengthOrPlatform: data.lengthOrPlatform || "",
    musicGenre: data.musicGenre || "",
    preferredDesignerIds: data.preferredDesignerIds || [],
    requestName: data.name || "",
    restrictAiAssets: data.restrictAiAssets ?? false,
    sizes: data.sizesNeeded || "",
    style: data.styleOfVideo || "",
    videoType: data.designTypeId || undefined,
  };
}

function mapWebsiteDesignDraftRequestToFormData(
  data: RequestInfo,
): DefaultValues<CreateWebsiteDesignRequestFormData> {
  const briefCreatorType = determineBriefCreatorType(data);

  return {
    assets: data.assets || [],
    brandId: data.brandId || undefined,
    briefCreatorDetailed: {
      additionalGuidelines:
        data.magicalBriefCreator?.additionalGuidelines || "",
      brandGuidelines: data.magicalBriefCreator?.brandGuidelines || "",
      creativeGoal: data.magicalBriefCreator?.creativeGoals || "",
      finalBrief: data.briefText || "",
      finalBriefHTML: data.briefText || "",
      scope: data.magicalBriefCreator?.scope || "",
      textsForDesign: data.magicalBriefCreator?.textForDesign || "",
    },
    briefCreatorSingle: {
      requestDescription: data.description || "",
      requestDescriptionHTML: data.description || "",
      textsForDesign: data.textForDesign || "",
      textsForDesignHTML: data.textForDesign || "",
    },
    briefCreatorType,
    collaboratorIds: data.collaboratorsIds || [],
    creativeDirection: data.creativeDirection || undefined,
    designComplexityId: data.complexityId || undefined,
    designTypeId: data.designTypeId || undefined,
    inspirations: data.inspirations || [],
    preferredDesignerIds: data.preferredDesignerIds || [],
    preferredFileTypes: data.preferredFileType || "",
    requestName: data.name || "",
    restrictAiAssets: data.restrictAiAssets ?? false,
    sizesNeeded: data.sizesNeeded || "",
  };
}

function mapWebsiteImplementationDraftRequestToFormData(
  data: RequestInfo,
): DefaultValues<CreateWebsiteImplementationRequestFormData> {
  return {
    assets: data.assets || [],
    brandId: data.brandId || undefined,
    collaboratorIds: data.collaboratorsIds || [],
    designComplexityId: data.complexityId || undefined,
    designTypeId: data.designTypeId || undefined,
    inspirations: data.inspirations || [],
    preferredDesignerIds: data.preferredDesignerIds || [],
    requestDescription: data.description || "",
    requestDescriptionHTML: data.description || "",
    requestName: data.name || "",
  };
}

function determineBriefCreatorType(data: RequestInfo) {
  if (data.briefText) return BriefCreatorTypeEnum.DETAILED;

  const mbc = data.magicalBriefCreator;

  if (mbc) {
    const hasMBCContent = [
      mbc.scope,
      mbc.creativeGoals,
      mbc.keyMessageAndCta,
      mbc.script,
      mbc.textForDesign,
      mbc.brandGuidelines,
      mbc.additionalGuidelines,
    ].some(Boolean);

    if (hasMBCContent) return BriefCreatorTypeEnum.DETAILED;
  }

  return BriefCreatorTypeEnum.SINGLE;
}
